import { Stack, Alert, Divider, Typography } from "@mui/material";
import {
  useWebApiV1GetUserFeatureSwitchesQuery,
  useWebApiV1ListSiteGroupsQuery,
  useWebApiV1OrderQuery,
} from "@providers/hop-ord-server/api";
import { useParams, Route, Routes } from "react-router-dom";
import { toLocaleDateTimeString } from "@utils";
import OrderTrackerDrawer from "../OrderTracker/components/OrderTrackerDrawer/OrderTrackerDrawer";
import PortaledMainBanner from "@pages/Layout/PortaledMainBanner";
import WarningIcon from "@mui/icons-material/Warning";
import WorkflowPageWrapper from "@pages/Layout/WorkflowPageWrapper";
import {
  PlanningTemplateApi,
  AutomatedPlanStatusPage,
  PlanReviewPage,
} from "./pages";
import HandoverPage from "@pages/OrderTracker/HandoverPage";
import DrawerControlButtons from "./components/DrawerControlButtons";
import { Feature } from "@enums";
import useOrderTrackerDrawer from "./components/OrderTrackerDrawer/useOrderTrackerDrawer";

const PAGE_WIDTH = "900px"; // Need to enforce the width so the PDF generation is consistent

const DosimetristPlanningAutomation = () => {
  const { orderId: orderIdString } = useParams();
  const orderId = Number(orderIdString);

  const {
    drawerOpen,
    setDrawerOpen,
    selectedDrawerPanel,
    setSelectedDrawerPanel,
  } = useOrderTrackerDrawer();
  const { data: orderData, isLoading: orderLoading } = useWebApiV1OrderQuery({
    orderId,
  });

  const { isError: siteGroupDataError, isFetching: siteGroupDataFetching } =
    useWebApiV1ListSiteGroupsQuery({
      orderId,
    });

  const { data: featureSwitchData, isLoading: featureSwitchesLoading } =
    useWebApiV1GetUserFeatureSwitchesQuery();

  const editingDatetime = orderData?.editingDatetime
    ? toLocaleDateTimeString(orderData?.editingDatetime || "")
    : "";

  const showPlanReview = featureSwitchData?.find(
    (feature) => feature.name === Feature.PLAN_REVIEW,
  );

  if (featureSwitchesLoading || orderLoading) {
    return <></>;
  }

  // TODO: remove this jank when plan_review_page feature switch is removed
  const planReviewPageTitles = location.pathname.endsWith("/handover")
    ? "Step 4: Handover"
    : location.pathname.endsWith("/plan-review")
      ? "Step 3: Plan Review"
      : "Step 2: Plan Generation";

  const nonPlanReviewPageTitles = location.pathname.endsWith("/handover")
    ? "Step 3: Handover"
    : "Step 2: Plan Generation";

  const planRevewIndex = location.pathname.endsWith("/handover")
    ? 3
    : location.pathname.endsWith("/plan-review")
      ? 2
      : 1;
  const nonPlanReviewIndex = location.pathname.endsWith("/handover") ? 2 : 1;

  const pageTitle = showPlanReview
    ? planReviewPageTitles
    : nonPlanReviewPageTitles;
  const currentPageIndex = showPlanReview ? planRevewIndex : nonPlanReviewIndex;

  return (
    <WorkflowPageWrapper currentPageIndex={currentPageIndex} orderId={orderId}>
      {orderData && !orderData?.isLatest && (
        <PortaledMainBanner>
          <Stack>
            <Alert
              severity="warning"
              sx={{ justifyContent: "center" }}
              icon={<WarningIcon />}
            >
              <Typography>{`This order was last edited at ${editingDatetime} and has not yet been submitted. Click 'Edit' to continue.`}</Typography>
            </Alert>
            <Divider />
          </Stack>
        </PortaledMainBanner>
      )}
      <Stack
        sx={{
          maxWidth: PAGE_WIDTH,
          width: 1,
          marginLeft: "auto",
          marginRight: "auto",
          padding: 2,
          paddingTop: 3,
          gap: 2,
        }}
      >
        <Typography variant="h5">{pageTitle}</Typography>
        <DrawerControlButtons
          setDrawerOpen={setDrawerOpen}
          setSelectedDrawerPanel={setSelectedDrawerPanel}
        />

        {orderData && (
          <Stack
            sx={{
              width: 1,
              height: 1,
              marginTop: 2,
              marginBottom: 2,
              gap: 2,
            }}
          >
            <Routes>
              <Route
                path="/"
                index
                element={
                  <PlanningTemplateApi
                    orderId={orderId}
                    disablePlanGeneration={
                      siteGroupDataError || siteGroupDataFetching
                    }
                  />
                }
              />
              <Route
                path="/automated"
                element={
                  <AutomatedPlanStatusPage
                    orderId={orderId}
                    disablePlanGeneration={
                      siteGroupDataError || siteGroupDataFetching
                    }
                  />
                }
              />
              <Route path="/plan-review" element={<PlanReviewPage />} />
              <Route
                path="/handover"
                element={<HandoverPage order={orderData} />}
              />
            </Routes>
          </Stack>
        )}
      </Stack>
      <OrderTrackerDrawer
        orderId={orderId}
        open={drawerOpen}
        setOpen={setDrawerOpen}
        selectedPanel={selectedDrawerPanel}
        setSelectedPanel={setSelectedDrawerPanel}
        orderData={orderData}
      />
    </WorkflowPageWrapper>
  );
};

export default DosimetristPlanningAutomation;
